import React, { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import {
	getPartnerCompanyEvent,
	getPartnerCompanyEventTaskById,
	getPartnerCompanyEventDocuments,
} from 'src/api/partner';
import { TaskViewer } from '@repo/components';
import { Spinner } from '@repo/components';
import { Alert } from '@repo/components';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { NotFound } from '@repo/components';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/approvals/$taskId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId, taskId } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	useBreadcrumbLevel(3, 'Approvals', `/partner/${partnerId}/events/${eventId}/approvals`);
	useBreadcrumbLevel(4, 'Approval Details');

	// Get event data
	const {
		data: event,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get task data
	const {
		data: task,
		isLoading: taskLoading,
		error: taskError,
	} = useQuery({
		queryKey: ['partner-company-event-task', partnerId, eventId, taskId],
		queryFn: () => getPartnerCompanyEventTaskById(partnerId, eventId, taskId),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	// Get event documents
	const { data: documentsResponse } = useQuery({
		queryKey: ['partner-company-event-documents', partnerId, eventId],
		queryFn: () => getPartnerCompanyEventDocuments(partnerId, eventId, constructQuery({ noPagination: 'true' })),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	const documents = documentsResponse?.data || [];
	const filteredDocuments = task?.documentIds ? documents.filter((d) => task.documentIds?.includes(d._id)) : [];

	const handleUpdate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event', partnerId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-approvals', partnerId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-task', partnerId, eventId, taskId] });
	}, [queryClient, partnerId, eventId, taskId]);

	const handleClose = useCallback(() => {
		navigate({ to: `/partner/${partnerId}/events/${eventId}/approvals` });
	}, [navigate, partnerId, eventId]);

	const handleOpenDocument = useCallback(
		(documentId: string) => {
			navigate({ to: `/partner/${partnerId}/events/${eventId}/documents/${documentId}` });
		},
		[navigate, partnerId, eventId],
	);

	if (userLoading || eventLoading || taskLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !event) {
		return (
			<div className="p-6">
				<Alert variant="destructive">Event not found</Alert>
			</div>
		);
	}

	if (taskError || !task) {
		return (
			<NotFound>
				<div className="w-[300px]">
					<NotFound.Illustration src="/public/empty_box.svg" />
				</div>
				<NotFound.Label>We couldn't find this task. Maybe it has been deleted?</NotFound.Label>
				<NotFound.Actions>
					<NotFound.Actions.Action
						onClick={() => navigate({ to: `/partner/${partnerId}/events/${eventId}/approvals` })}
						variant="outlined"
					>
						Return to approvals
					</NotFound.Actions.Action>
				</NotFound.Actions>
			</NotFound>
		);
	}

	return (
		<TaskViewer
			task={task}
			documents={filteredDocuments}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDo: task.executePermission?.isAllowed,
				taskDelete: verifyPermission(PERMISSIONS.partnerCompanyEventsEdit),
			}}
		/>
	);
}
